import React, { useEffect, useState } from "react"

import { Field, Form, Formik } from "formik"
import moment from "moment-timezone"
import Select from "react-select"

import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import {
  billOptions,
  durationChangeOptions,
  payerOptions,
  regionOptions,
  statusOptions,
} from "src/enums"

import { gql, useQuery } from "hooks/urql"

import AutocompleteSelect from "components/Forms/AutocompleteSelect"
import Checkbox from "components/Forms/Checkbox"
import { DateRangePicker } from "components/Forms/DatePicker"
import { SelectField } from "components/Forms/Formik/hookComponents"
import { Loading } from "components/Icons"
import LocalTime from "components/LocalTime"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import Export from "./Export"

const SearchAirtutorsSessions = ({ canAdminBudgets }) => {
  const [page, setPage] = useState(1)
  const [queryVariables, setQueryVariables] = useState({})
  const [result, fetchQuery] = useQuery({
    query: searchSessionsQuery,
    variables: { page: page, searchInput: queryVariables },
    pause: true,
  })

  useEffect(() => {
    if (JSON.stringify(queryVariables) === "{}") return

    fetchQuery()
  }, [queryVariables, fetchQuery])

  const formState = {
    tutorIds: [],
    studentIds: [],
    teacherIds: [],
    organizationIds: [],
    schoolIds: [],
    subjectIds: [],
    statusStates: [],
    billStates: [],
    durationChangeStates: [],
    payerType: undefined,
    startsAt: undefined,
    endsAt: undefined,
    minDuration: "",
    maxDuration: "",
    noShow: false,
    studentTagList: [],
    organizationRegion: "",
  }

  const handleSubmit = values => {
    setPage(1)
    const variables = { ...values }
    if (variables.startsAt) {
      variables.startsAt = moment(variables.startsAt.toDate())
        .startOf("day")
        .format()
    }
    if (variables.endsAt) {
      variables.endsAt = moment(variables.endsAt.toDate()).endOf("day").format()
    }
    setQueryVariables(variables)
  }

  return (
    <>
      <Formik initialValues={formState} onSubmit={handleSubmit}>
        {({ status, setFieldValue }) => (
          <Form>
            <div className="flex flex-wrap gap-4">
              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Tutor
                  <AutocompleteSelect
                    api="/api/admins/tutors/autocomplete_full_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "tutorIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Student
                  <AutocompleteSelect
                    api="/api/admins/students/autocomplete_full_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "studentIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Teacher
                  <AutocompleteSelect
                    api="/api/admins/teachers/autocomplete_full_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "teacherIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Organization
                  <AutocompleteSelect
                    api="/api/admins/organizations/autocomplete_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "organizationIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  School
                  <AutocompleteSelect
                    api="/api/admins/schools/autocomplete_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "schoolIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Subject
                  <AutocompleteSelect
                    api="/api/admins/subjects/autocomplete_name"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "subjectIds",
                        selected.map(s => s?.value)
                      )
                    }
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Scheduled
                  <Select
                    options={statusOptions}
                    isClearable
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "statusStates",
                        selected.map(s => s?.value)
                      )
                    }
                    styles={{
                      container: provided => ({
                        ...provided,
                        maxWidth: 250,
                      }),
                    }}
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Billed
                  <Select
                    options={billOptions}
                    isClearable
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "billStates",
                        selected.map(s => s?.value)
                      )
                    }
                    styles={{
                      container: provided => ({
                        ...provided,
                        maxWidth: 250,
                      }),
                    }}
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Duration Change
                  <Select
                    options={durationChangeOptions}
                    isClearable
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "durationChangeStates",
                        selected.map(s => s?.value)
                      )
                    }
                    styles={{
                      container: provided => ({
                        ...provided,
                        maxWidth: 250,
                      }),
                    }}
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Payer Type
                  <Select
                    options={payerOptions}
                    isClearable
                    onChange={selected =>
                      setFieldValue("payerType", selected?.value)
                    }
                    styles={{
                      container: provided => ({
                        ...provided,
                        maxWidth: 250,
                      }),
                    }}
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Date Range
                  <DateRangePicker
                    onChange={([startDate, endDate]) => {
                      setFieldValue("startsAt", startDate)
                      setFieldValue("endsAt", endDate)
                    }}
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Minimum Duration
                  <Field
                    name="minDuration"
                    type="text"
                    className="form-control"
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Maximum Duration
                  <Field
                    name="maxDuration"
                    type="text"
                    className="form-control"
                  />
                </label>
              </div>

              <div className="form-group min-w-[200px]">
                <label className="form-label w-100">
                  Student Tags
                  <AutocompleteSelect
                    api="/api/admins/students/autocomplete_tags"
                    isMulti
                    onChange={selected =>
                      setFieldValue(
                        "studentTagList",
                        selected.map(tag => tag.label)
                      )
                    }
                  />
                </label>
              </div>

              <SelectField
                name="organizationRegion"
                label="Organization Region"
                options={regionOptions}
                isClearable
              />
            </div>

            <div className="form-group min-w-[200px]">
              <label className="form-label w-100">
                <Field name="noShow">
                  {({ field: { onChange, name, value } }) => (
                    <Checkbox
                      name={name}
                      onChange={onChange}
                      checked={value}
                      value={true}
                    >
                      No Show
                    </Checkbox>
                  )}
                </Field>
              </label>
            </div>

            <div className="form-group min-w-[200px]">
              <label className="form-label w-100">
                <Field name="onDemand">
                  {({ field: { onChange, name, value } }) => (
                    <Checkbox
                      name={name}
                      onChange={onChange}
                      checked={value}
                      value={true}
                    >
                      On Demand
                    </Checkbox>
                  )}
                </Field>
              </label>
            </div>

            {status && <div className="alert alert-danger mt-4">{status}</div>}

            <div className="inline-lock mt-4 mr-4">
              <button
                type="submit"
                className="btn solid btn-info"
                disabled={result.fetching}
              >
                <span>Search</span>
                {result.fetching && (
                  <div className="ml-4 inline-block">
                    <Loading />
                  </div>
                )}
              </button>
            </div>

            <UrqlLoadingIndicator result={result}>
              {({ data }) => (
                <>
                  <Export canAdminBudgets={canAdminBudgets} />

                  <table className="table">
                    <thead className="text-primary">
                      <tr>
                        <th>Subject</th>
                        <th>Tutor</th>
                        <th>Students</th>
                        <th>Study Group</th>
                        <th>Date</th>
                        <th>Duration</th>
                        <th>No Show?</th>
                        <th>Scheduled State</th>
                        <th>Bill State</th>
                        <th>Payer Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.airtutorsSessions.data.map(session => (
                        <tr
                          key={session.id}
                          className="cursor-pointer hover:bg-white"
                          onClick={() =>
                            window.open(session.showPath, "_blank")
                          }
                        >
                          <td>{session.subject.name}</td>
                          <td>
                            {session.tutor && (
                              <a
                                href={session.tutor.showPath}
                                onClick={e => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {session.tutor.fullName}
                              </a>
                            )}
                          </td>
                          <td>
                            <ul className="list-unstyled">
                              {session.students.map(student => (
                                <li key={student.id}>
                                  <a
                                    href={student.showPath}
                                    onClick={e => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {student.fullName}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td>
                            {session.studyGroup && (
                              <a
                                href={session.studyGroup.showPath}
                                onClick={e => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {session.studyGroup.name}
                              </a>
                            )}
                          </td>
                          <td>
                            <div className="flex flex-col">
                              <LocalTime
                                timestamp={session.startsAt}
                                omitTime={true}
                              />
                              <span>
                                <LocalTime
                                  timestamp={session.startsAt}
                                  format="LT"
                                />
                                &nbsp;&mdash;&nbsp;
                                <LocalTime
                                  timestamp={session.endsAt}
                                  format="LT"
                                />
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="flex flex-col">
                              <span>
                                Actual: {session.formattedActualDuration}
                              </span>
                              <span>
                                Billed: {session.formattedBilledDuration}
                              </span>
                              <span>{session.durationChangeState}</span>
                            </div>
                          </td>
                          <td>
                            {session.noShow && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="text-danger mr-3"
                              />
                            )}
                          </td>
                          <td>{session.statusState}</td>
                          <td>{session.billState}</td>
                          <td>
                            {session.organization ? (
                              <a
                                href={session.organization.showPath}
                                onClick={e => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {session.organization.name}
                              </a>
                            ) : (
                              session.payerType
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={data.airtutorsSessions.currentPage}
                    totalPages={data.airtutorsSessions.totalPages}
                    changePage={page => setPage(page)}
                  />
                </>
              )}
            </UrqlLoadingIndicator>
          </Form>
        )}
      </Formik>
    </>
  )
}

const searchSessionsQuery = gql`
  query AdminSessionSearch(
    $page: Int
    $searchInput: AirtutorsSessionSearchInputObject!
  ) {
    airtutorsSessions(page: $page, searchInput: $searchInput) {
      totalPages
      currentPage
      data {
        id
        startsAt
        endsAt
        formattedActualDuration
        formattedBilledDuration
        noShow
        statusState
        billState
        payerType
        durationChangeState
        showPath

        organization {
          id
          name
          showPath
        }
        subject {
          name
        }
        tutor {
          fullName
          showPath
        }
        students {
          id
          fullName
          showPath
        }
        studyGroup {
          id
          name
          showPath
        }
      }
    }
  }
`

export default SearchAirtutorsSessions
